import { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Loader2, TrendingUp, Clock, Eye, Wallet, Trophy } from 'lucide-react';

import { QuoteLayout } from '@/components/quotes/QuoteLayout';
import { formatMinor } from '@/lib/quoteMoney';

const API_URL = process.env.REACT_APP_API_URL;
const PERIODS = [30, 90, 365];
const BAR = '#2563EB';

function formatHours(hours, t) {
  if (hours == null) return '—';
  if (hours < 1) return t('insights.minutes', { count: Math.max(1, Math.round(hours * 60)) });
  if (hours < 48) return t('insights.hours', { count: Math.round(hours) });
  return t('insights.days', { count: Math.round(hours / 24) });
}

function moneyLines(byCurrency, locale) {
  const entries = Object.entries(byCurrency || {}).filter(([, v]) => v > 0);
  if (!entries.length) return ['—'];
  return entries.map(([cur, minor]) => formatMinor(minor, cur, locale));
}

function StatTile({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E5E5E5] p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs font-medium text-[#64748B]">
        <Icon className="w-4 h-4 text-[#2563EB]" /> {label}
      </div>
      <div className="mt-2 space-y-0.5">
        {(Array.isArray(value) ? value : [value]).map((v, i) => (
          <p key={i} className="text-2xl font-bold text-[#1A1A2E] tabular-nums">{v}</p>
        ))}
      </div>
      {sub && <p className="mt-1 text-xs text-[#94A3B8]">{sub}</p>}
    </div>
  );
}

/** Single-series horizontal bars: one hue, value labelled at the bar end. */
function Bars({ rows, testId }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="space-y-3" data-testid={testId}>
      {rows.map((r) => (
        <li key={r.key} className="grid grid-cols-[120px_1fr_40px] items-center gap-3 text-sm sm:grid-cols-[160px_1fr_48px]">
          <span className="truncate text-[#475569]">{r.label}</span>
          <span className="relative h-4" title={`${r.label}: ${r.value}`}>
            <span
              className="absolute inset-y-0 left-0 rounded-r"
              style={{ width: `${(r.value / max) * 100}%`, minWidth: r.value ? 4 : 0, background: BAR }}
            />
          </span>
          <span className="text-right font-semibold text-[#1A1A2E] tabular-nums">{r.value}</span>
        </li>
      ))}
    </ul>
  );
}

export function QuoteInsights() {
  const { t, i18n } = useTranslation('quotes');
  const [days, setDays] = useState(90);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);
  const locale = { tr: 'tr-TR', en: 'en-GB', de: 'de-DE', fr: 'fr-FR', es: 'es-ES' }[(i18n.language || 'tr').slice(0, 2)] || 'en-GB';

  useEffect(() => {
    let cancelled = false;
    setError(false);
    axios.get(`${API_URL}/api/quote/insights/stats`, { params: { days } })
      .then(({ data }) => { if (!cancelled) setStats(data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [days]);

  const pct = (v) => (v == null ? '—' : `%${v}`);
  const statusRows = stats ? [
    { key: 'sent', label: t('insights.status.open'), value: (stats.by_status.sent || 0) + (stats.by_status.viewed || 0) },
    { key: 'accepted', label: t('insights.status.accepted'), value: stats.by_status.accepted || 0 },
    { key: 'revision_requested', label: t('insights.status.revision'), value: stats.by_status.revision_requested || 0 },
    { key: 'rejected', label: t('insights.status.rejected'), value: stats.by_status.rejected || 0 },
    { key: 'draft', label: t('insights.status.draft'), value: stats.by_status.draft || 0 },
  ] : [];
  const packageRows = stats ? ['economic', 'recommended', 'premium'].map((key) => ({
    key, label: t(`insights.packages.${key}`), value: stats.package_choices?.[key] || 0,
  })) : [];

  return (
    <QuoteLayout>
      <div className="max-w-5xl mx-auto pb-24" data-testid="quote-insights-page">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A2E]">{t('insights.title')}</h1>
            <p className="text-sm text-[#595959] mt-1">{t('insights.subtitle')}</p>
          </div>
          <div className="inline-flex rounded-lg border border-[#E5E5E5] bg-white p-1" role="group">
            {PERIODS.map((d) => (
              <button key={d} type="button" onClick={() => setDays(d)} aria-pressed={days === d}
                className={`px-3 py-1.5 text-sm rounded-md ${days === d ? 'bg-[#2563EB] text-white' : 'text-[#475569] hover:bg-[#F1F5F9]'}`}
                data-testid={`insights-period-${d}`}>
                {t('insights.lastDays', { count: d })}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="text-sm text-rose-700">{t('common.error')}</p>}
        {!stats && !error && (
          <div className="p-10 text-center text-[#64748B]"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        )}
        {stats && stats.created === 0 && (
          <div className="bg-white rounded-2xl border border-[#E5E5E5] p-8 text-center text-[#64748B]" data-testid="insights-empty">
            {t('insights.empty')}
          </div>
        )}
        {stats && stats.created > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <StatTile icon={Trophy} label={t('insights.winRate')} value={pct(stats.win_rate)}
                sub={t('insights.winRateHint', { accepted: stats.by_status.accepted || 0, rejected: stats.by_status.rejected || 0 })} testId="insights-win-rate" />
              <StatTile icon={TrendingUp} label={t('insights.acceptanceRate')} value={pct(stats.acceptance_rate)}
                sub={t('insights.acceptanceRateHint', { sent: stats.sent })} testId="insights-acceptance" />
              <StatTile icon={Eye} label={t('insights.viewRate')} value={pct(stats.view_rate)}
                sub={t('insights.avgToView', { time: formatHours(stats.avg_hours_to_first_view, t) })} testId="insights-view-rate" />
              <StatTile icon={Clock} label={t('insights.avgResponse')} value={formatHours(stats.avg_hours_to_response, t)}
                sub={t('insights.avgResponseHint')} testId="insights-response" />
              <StatTile icon={Wallet} label={t('insights.pending')} value={moneyLines(stats.pending_value_minor, locale)}
                sub={t('insights.pendingHint')} testId="insights-pending" />
              <StatTile icon={Trophy} label={t('insights.won')} value={moneyLines(stats.won_value_minor, locale)}
                sub={t('insights.wonHint')} testId="insights-won" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              <section className="bg-white rounded-2xl border border-[#E5E5E5] p-5">
                <h2 className="text-sm font-semibold text-[#1A1A2E]">{t('insights.statusTitle', { count: stats.created })}</h2>
                <div className="mt-4"><Bars rows={statusRows} testId="insights-status-bars" /></div>
              </section>
              <section className="bg-white rounded-2xl border border-[#E5E5E5] p-5">
                <h2 className="text-sm font-semibold text-[#1A1A2E]">{t('insights.packagesTitle')}</h2>
                {stats.multi_package_accepted > 0 ? (
                  <div className="mt-4"><Bars rows={packageRows} testId="insights-package-bars" /></div>
                ) : (
                  <p className="mt-4 text-sm text-[#64748B]">{t('insights.packagesEmpty')}</p>
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </QuoteLayout>
  );
}

export default QuoteInsights;
